import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import AuthDebug from '../components/AuthDebug';
import SuperAdminLayout from '../components/SuperAdminLayout';
import ImageUpload from '../components/ImageUpload';

const SuperAdminSites = () => {
  const [sites, setSites] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('all');
  const [selectedSiteForImages, setSelectedSiteForImages] = useState(null);

  useEffect(() => {
    fetchSites();
    fetchTenants();
  }, []);

  const fetchSites = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/super-admin/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data.sites);
    } catch (error) {
      console.error('Error fetching sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/super-admin/sites/tenants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.tenants.length === 0) {
        // Fallback to hardcoded tenants
        setTenants([
          { _id: '68fbc468677e7e1d89957085', name: 'Seven Wonders Park', subdomain: 'sevenwonders' },
          { _id: '68fbc468677e7e1d89957086', name: 'Heritage Temples', subdomain: 'heritage' }
        ]);
      } else {
        setTenants(response.data.tenants);
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      // Fallback to hardcoded tenants
      setTenants([
        { _id: '68fbc468677e7e1d89957085', name: 'Seven Wonders Park', subdomain: 'sevenwonders' },
        { _id: '68fbc468677e7e1d89957086', name: 'Heritage Temples', subdomain: 'heritage' }
      ]);
    }
  };

  const handleAssignTenant = async (siteId, tenantId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/super-admin/sites/${siteId}/assign`, 
        { tenantId }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchSites();
    } catch (error) {
      alert('Error assigning tenant: ' + (error.response?.data?.message || error.message));
    }
  };

  const filteredSites = sites.filter(site => {
    const matchesSearch = site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         site.city.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (!matchesSearch) return false;
    
    if (cityFilter !== 'all' && site.city !== cityFilter) return false;
    
    if (filter === 'all') return true;
    if (filter === 'assigned') return site.tenantId;
    if (filter === 'unassigned') return !site.tenantId;
    return site.tenantId?._id === filter;
  });

  const uniqueCities = [...new Set(sites.map(site => site.city))].sort();

  const getStatusColor = (site) => {
    if (!site.tenantId) return '#f56565';
    return '#48bb78';
  };

  const getFeatureIcon = (feature) => {
    const icons = {
      'entry_ticket': '🎫',
      'parking_ticket': '🅿️',
      'chappal_stand_ticket': '👡',
      'puja_ticket': '🙏',
      'event_ticket': '🎭',
      'other': '🏛️'
    };
    return icons[feature] || '🏛️';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div style={{ fontSize: '18px' }}>Loading sites...</div>
      </div>
    );
  }

  return (
    <SuperAdminLayout>
      <AuthDebug />
      <header style={{
        background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 50%, #10b981 100%)',
        color: 'white',
        padding: '16px 20px',
        boxShadow: '0 4px 15px rgba(6, 182, 212, 0.2)'
      }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: '700', margin: '0 0 4px 0' }}>Sites Management</h1>
        <p style={{ color: 'rgba(255,255,255,0.8)', margin: 0, fontSize: '0.85rem' }}>Assign sites to tenants</p>
      </header>
      
      <div style={{ padding: '16px' }}>

      {/* Compact Search and Filters */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '12px', display: 'flex', gap: '12px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search sites..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '200px',
              padding: '6px 10px',
              border: '2px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '8px',
              fontSize: '13px'
            }}
          />
          <select
            value={cityFilter}
            onChange={(e) => setCityFilter(e.target.value)}
            style={{
              padding: '6px 10px',
              border: '2px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '8px',
              fontSize: '13px',
              minWidth: '120px'
            }}
          >
            <option value="all">All Cities</option>
            {uniqueCities.map(city => (
              <option key={city} value={city}>
                {city.charAt(0).toUpperCase() + city.slice(1)}
              </option>
            ))}
          </select>
        </div>
        
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            onClick={() => setFilter('all')}
            style={{
              background: filter === 'all' ? 'linear-gradient(135deg, #06b6d4, #10b981)' : '#e2e8f0',
              color: filter === 'all' ? 'white' : '#4a5568',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            All ({sites.length})
          </button>
          <button
            onClick={() => setFilter('unassigned')}
            style={{
              background: filter === 'unassigned' ? '#ef4444' : '#e2e8f0',
              color: filter === 'unassigned' ? 'white' : '#4a5568',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            Unassigned ({sites.filter(s => !s.tenantId).length})
          </button>
          <button
            onClick={() => setFilter('assigned')}
            style={{
              background: filter === 'assigned' ? '#10b981' : '#e2e8f0',
              color: filter === 'assigned' ? 'white' : '#4a5568',
              border: 'none',
              padding: '4px 8px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            Assigned ({sites.filter(s => s.tenantId).length})
          </button>
        </div>
      </div>

      {/* Compact Sites Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
        {filteredSites.map(site => (
          <div key={site._id} style={{
            background: 'white',
            borderRadius: '10px',
            padding: '12px',
            boxShadow: '0 4px 15px rgba(6, 182, 212, 0.1)',
            border: '1px solid rgba(6, 182, 212, 0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px' }}>
              <h3 style={{ margin: 0, color: '#2d3748', fontSize: '16px', lineHeight: '1.3' }}>{site.name}</h3>
              <div style={{
                background: getStatusColor(site),
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: '600',
                whiteSpace: 'nowrap'
              }}>
                {site.tenantId ? '✓ Assigned' : '⚠ Unassigned'}
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginBottom: '15px', flexWrap: 'wrap' }}>
              {site.features && site.features.map(feature => (
                <span key={feature} style={{
                  background: '#667eea',
                  color: 'white',
                  padding: '4px 8px',
                  borderRadius: '4px',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {getFeatureIcon(feature)} {feature.replace('_', ' ')}
                </span>
              ))}
            </div>
            
            <div style={{ fontSize: '14px', color: '#4a5568', marginBottom: '15px' }}>
              <div style={{ marginBottom: '5px' }}>
                📍 {site.city.charAt(0).toUpperCase() + site.city.slice(1)}, {site.state || 'Odisha'}
              </div>
              <div style={{ marginBottom: '10px', fontSize: '13px', color: '#718096' }}>
                {site.description || 'No description available'}
              </div>
              
              <div style={{
                background: '#f7fafc',
                padding: '10px',
                borderRadius: '6px',
                border: `2px solid ${getStatusColor(site)}`,
                marginBottom: '15px'
              }}>
                <div style={{ fontWeight: '600', color: getStatusColor(site), marginBottom: '5px' }}>
                  {site.tenantId ? `Assigned to ${site.tenantId.name}` : 'Unassigned'}
                </div>
                {site.tenantId && (
                  <div style={{ fontSize: '12px', color: '#718096' }}>
                    Subdomain: {site.tenantId.subdomain}
                  </div>
                )}
              </div>
            </div>

            {/* Site Images Preview */}
            {site.images && site.images.length > 0 && (
              <div style={{ marginBottom: '15px' }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>Images ({site.images.length})</div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {site.images.slice(0, 3).map((image, index) => (
                    <img
                      key={index}
                      src={`${API_BASE_URL}${image.path}`}
                      alt={image.originalName}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '6px',
                        border: '2px solid #e2e8f0'
                      }}
                    />
                  ))}
                  {site.images.length > 3 && (
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: '#f7fafc',
                      border: '2px solid #e2e8f0',
                      borderRadius: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      color: '#4a5568'
                    }}>
                      +{site.images.length - 3}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
              <button
                onClick={() => setSelectedSiteForImages(site)}
                style={{
                  flex: 1,
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                📷 Manage Images
              </button>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                Assign to Tenant:
              </label>
              <select
                value={site.tenantId?._id || ''}
                onChange={(e) => handleAssignTenant(site._id, e.target.value || null)}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">-- Unassigned --</option>
                {tenants.map(tenant => (
                  <option key={tenant._id} value={tenant._id}>
                    {tenant.name} ({tenant.subdomain})
                  </option>
                ))}
              </select>
            </div>
          </div>
        ))}
      </div>

      {filteredSites.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#f7fafc',
          borderRadius: '12px',
          border: '2px dashed #cbd5e0'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '20px' }}>🏛️</div>
          <h3 style={{ color: '#4a5568', marginBottom: '10px' }}>
            {searchTerm ? 'No sites found matching your search' : 'No Sites Found'}
          </h3>
          <p style={{ color: '#718096' }}>
            {searchTerm 
              ? `Try searching for different terms or clear the search filter`
              : 'Religious sites will appear here once they are added to the system'
            }
          </p>
        </div>
      )}

      {/* Compact Summary Stats */}
      <div style={{
        marginTop: '16px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '12px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #06b6d4, #10b981)',
          color: 'white',
          padding: '12px',
          borderRadius: '10px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>🏛️</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{sites.length}</div>
          <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Total Sites</div>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #10b981, #14b8a6)',
          color: 'white',
          padding: '12px',
          borderRadius: '10px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>✅</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{sites.filter(s => s.tenantId).length}</div>
          <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Assigned</div>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #ef4444, #dc2626)',
          color: 'white',
          padding: '12px',
          borderRadius: '10px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>⚠️</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{sites.filter(s => !s.tenantId).length}</div>
          <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Unassigned</div>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #0891b2, #06b6d4)',
          color: 'white',
          padding: '12px',
          borderRadius: '10px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '1.2rem', marginBottom: '4px' }}>🏢</div>
          <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{tenants.length}</div>
          <div style={{ opacity: 0.9, fontSize: '0.8rem' }}>Tenants</div>
        </div>
      </div>

      {/* Image Upload Modal */}
      {selectedSiteForImages && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '30px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0 }}>Manage Images - {selectedSiteForImages.name}</h3>
              <button
                onClick={() => setSelectedSiteForImages(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#718096'
                }}
              >
                ×
              </button>
            </div>
            
            <ImageUpload
              siteId={selectedSiteForImages._id}
              tenantId={selectedSiteForImages.tenantId?._id || '68fbc468677e7e1d89957085'}
              existingImages={selectedSiteForImages.images || []}
              onImagesUploaded={() => {
                fetchSites();
                setSelectedSiteForImages(null);
              }}
            />
          </div>
        </div>
      )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminSites;